/**
 * Pathfinding & Distance Utilities
 * 8-directional A* pathfinding, reachability, line-of-sight, and Chebyshev distance.
 */

import type { GridMap } from './types';
import { inBounds, blocksLight, coordKey, getCell } from './grid';

// ============================================================================
// TYPES
// ============================================================================

export interface TilePos {
  x: number;
  y: number;
}

export interface ReachableTile {
  x: number;
  y: number;
  cost: number;
  path: TilePos[];
}

// 8 directions: cardinal + diagonal
const DIRS_8: Array<{ dx: number; dy: number }> = [
  { dx: 0, dy: -1 },  // N
  { dx: 1, dy: -1 },  // NE
  { dx: 1, dy: 0 },   // E
  { dx: 1, dy: 1 },   // SE
  { dx: 0, dy: 1 },   // S
  { dx: -1, dy: 1 },  // SW
  { dx: -1, dy: 0 },  // W
  { dx: -1, dy: -1 }, // NW
];

// ============================================================================
// DISTANCE
// ============================================================================

/** Chebyshev distance — diagonal movement costs the same as cardinal (1 step). */
export function tileDistance(x1: number, y1: number, x2: number, y2: number): number {
  return Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1));
}

// ============================================================================
// 8-DIRECTIONAL MOVEMENT
// ============================================================================

/**
 * Check if a tile is walkable (not wall, void, or closed door).
 * Note: open doors are walkable.
 */
function isWalkable(map: GridMap, x: number, y: number, openDoors?: Set<string>): boolean {
  if (!inBounds(map, x, y)) return false;
  const cell = map.cells[y][x];
  if (cell.terrain === 'wall' || cell.terrain === 'void') return false;
  if (cell.terrain === 'door' && !openDoors?.has(coordKey(x, y))) return false;
  return true;
}

/**
 * Check if a cardinal move from (x,y) in direction (dx,dy) is blocked by wall edges.
 * dx/dy must be cardinal (one of them must be 0).
 */
function isCardinalBlocked(map: GridMap, x: number, y: number, dx: number, dy: number, openDoors?: Set<string>): boolean {
  const cell = getCell(map, x, y);
  if (!cell) return true;

  // Map dx/dy to wall direction
  let wallDir: 'N' | 'E' | 'S' | 'W';
  if (dx === 0 && dy === -1) wallDir = 'N';
  else if (dx === 1 && dy === 0) wallDir = 'E';
  else if (dx === 0 && dy === 1) wallDir = 'S';
  else if (dx === -1 && dy === 0) wallDir = 'W';
  else return true; // not cardinal

  if (cell.walls[wallDir]) return true;

  const nx = x + dx;
  const ny = y + dy;
  return !isWalkable(map, nx, ny, openDoors);
}

/**
 * Check if diagonal movement from (x,y) by (dx,dy) is blocked.
 * Diagonal movement is blocked if:
 * 1. The destination tile is not walkable
 * 2. Both adjacent cardinal tiles are blocked (no corner-cutting)
 * 3. Wall edges block cardinal passage in both adjacent cardinal directions
 */
export function isBlockedDiagonal(
  map: GridMap,
  x: number, y: number,
  dx: number, dy: number,
  openDoors?: Set<string>,
): boolean {
  // Must be a diagonal move
  if (dx === 0 || dy === 0) return false;

  const nx = x + dx;
  const ny = y + dy;

  // Destination must be walkable
  if (!isWalkable(map, nx, ny, openDoors)) return true;

  // Corner-cutting check: both adjacent cardinal tiles must not be blocked
  // E.g., moving NE (dx=1, dy=-1): check N (0,-1) and E (1,0)
  const cardinalA_blocked = isCardinalBlocked(map, x, y, dx, 0, openDoors);
  const cardinalB_blocked = isCardinalBlocked(map, x, y, 0, dy, openDoors);

  // Blocked only if BOTH cardinal paths are blocked
  return cardinalA_blocked && cardinalB_blocked;
}

/**
 * Get all 8-directional walkable neighbors from (x,y).
 * Respects wall edges for cardinal movement and corner-cutting rules for diagonals.
 */
export function getNeighbors8(
  map: GridMap,
  x: number, y: number,
  openDoors?: Set<string>,
  blockedTiles?: Set<string>,
): TilePos[] {
  const result: TilePos[] = [];

  for (const { dx, dy } of DIRS_8) {
    const nx = x + dx;
    const ny = y + dy;
    if (!isWalkable(map, nx, ny, openDoors)) continue;
    if (blockedTiles?.has(coordKey(nx, ny))) continue;

    const isDiagonal = dx !== 0 && dy !== 0;

    if (isDiagonal) {
      if (isBlockedDiagonal(map, x, y, dx, dy, openDoors)) continue;
    } else {
      if (isCardinalBlocked(map, x, y, dx, dy, openDoors)) continue;
    }

    result.push({ x: nx, y: ny });
  }

  return result;
}

// ============================================================================
// A* PATHFINDING
// ============================================================================

interface AStarNode {
  x: number;
  y: number;
  g: number; // cost from start
  f: number; // g + heuristic
  parent: AStarNode | null;
}

/**
 * Find shortest path from `from` to `to` using 8-directional A*.
 * Returns array of tile positions (including start and end), or empty array if no path.
 * All steps cost 1 by default (Chebyshev — diagonal same as cardinal).
 * Optional tileCostFn overrides per-tile entry cost (e.g., water = 2).
 */
export function findPath(
  map: GridMap,
  from: TilePos,
  to: TilePos,
  openDoors?: Set<string>,
  blockedTiles?: Set<string>,
  tileCostFn?: (map: GridMap, x: number, y: number) => number,
): TilePos[] {
  if (from.x === to.x && from.y === to.y) return [{ x: from.x, y: from.y }];

  const toKey = coordKey(to.x, to.y);

  // Don't pathfind to a blocked tile
  if (blockedTiles?.has(toKey)) return [];
  if (!isWalkable(map, to.x, to.y, openDoors)) return [];

  const open: AStarNode[] = [];
  const closed = new Set<string>();
  const gScores = new Map<string, number>();

  const startNode: AStarNode = {
    x: from.x, y: from.y,
    g: 0,
    f: tileDistance(from.x, from.y, to.x, to.y),
    parent: null,
  };
  open.push(startNode);
  gScores.set(coordKey(from.x, from.y), 0);

  while (open.length > 0) {
    // Find node with lowest f score
    let bestIdx = 0;
    for (let i = 1; i < open.length; i++) {
      if (open[i].f < open[bestIdx].f) bestIdx = i;
    }
    const current = open[bestIdx];
    open.splice(bestIdx, 1);

    const currentKey = coordKey(current.x, current.y);

    if (currentKey === toKey) {
      // Reconstruct path
      const path: TilePos[] = [];
      let node: AStarNode | null = current;
      while (node) {
        path.push({ x: node.x, y: node.y });
        node = node.parent;
      }
      return path.reverse();
    }

    closed.add(currentKey);

    const neighbors = getNeighbors8(map, current.x, current.y, openDoors, blockedTiles);
    for (const n of neighbors) {
      const nKey = coordKey(n.x, n.y);
      if (closed.has(nKey)) continue;

      const tentativeG = current.g + (tileCostFn ? tileCostFn(map, n.x, n.y) : 1);
      const existingG = gScores.get(nKey);

      if (existingG !== undefined && tentativeG >= existingG) continue;

      gScores.set(nKey, tentativeG);
      const h = tileDistance(n.x, n.y, to.x, to.y);
      const newNode: AStarNode = {
        x: n.x, y: n.y,
        g: tentativeG,
        f: tentativeG + h,
        parent: current,
      };

      // Remove existing entry in open if present (we found a better path)
      const existingIdx = open.findIndex(o => coordKey(o.x, o.y) === nKey);
      if (existingIdx >= 0) open.splice(existingIdx, 1);

      open.push(newNode);
    }
  }

  return []; // No path found
}

// ============================================================================
// REACHABLE TILES
// ============================================================================

/**
 * Get all tiles reachable from `from` within a movement budget of `maxCost` steps.
 * Uses Dijkstra-style flood fill with 8-directional movement.
 * Returns map of coordKey → ReachableTile (excludes starting position).
 * Optional tileCostFn overrides per-tile entry cost (e.g., water = 2).
 */
export function getReachableTiles(
  map: GridMap,
  from: TilePos,
  maxCost: number,
  openDoors?: Set<string>,
  blockedTiles?: Set<string>,
  tileCostFn?: (map: GridMap, x: number, y: number) => number,
): Map<string, ReachableTile> {
  const result = new Map<string, ReachableTile>();
  const visited = new Map<string, number>(); // coordKey → best cost
  const parents = new Map<string, TilePos[]>(); // coordKey → best path

  interface QueueEntry { x: number; y: number; cost: number; path: TilePos[] }
  const queue: QueueEntry[] = [{ x: from.x, y: from.y, cost: 0, path: [{ x: from.x, y: from.y }] }];
  visited.set(coordKey(from.x, from.y), 0);

  while (queue.length > 0) {
    // Sort by cost (simple priority queue)
    let bestIdx = 0;
    for (let i = 1; i < queue.length; i++) {
      if (queue[i].cost < queue[bestIdx].cost) bestIdx = i;
    }
    const current = queue[bestIdx];
    queue.splice(bestIdx, 1);

    if (current.cost > maxCost) continue;

    const neighbors = getNeighbors8(map, current.x, current.y, openDoors, blockedTiles);
    for (const n of neighbors) {
      const nKey = coordKey(n.x, n.y);
      const newCost = current.cost + (tileCostFn ? tileCostFn(map, n.x, n.y) : 1);

      if (newCost > maxCost) continue;

      const existingCost = visited.get(nKey);
      if (existingCost !== undefined && newCost >= existingCost) continue;

      visited.set(nKey, newCost);
      const newPath = [...current.path, { x: n.x, y: n.y }];
      parents.set(nKey, newPath);
      queue.push({ x: n.x, y: n.y, cost: newCost, path: newPath });

      result.set(nKey, { x: n.x, y: n.y, cost: newCost, path: newPath });
    }
  }

  return result;
}

// ============================================================================
// LINE OF SIGHT (Bresenham)
// ============================================================================

/**
 * Check if there is unobstructed line of sight between two tiles.
 * Uses Bresenham's line algorithm, checking blocksLight() for each cell along the line.
 * Start and end tiles are NOT checked (you can always see yourself and the target).
 */
export function hasLineOfSight(
  map: GridMap,
  from: TilePos,
  to: TilePos,
  openDoors?: Set<string>,
): boolean {
  let x0 = from.x;
  let y0 = from.y;
  const x1 = to.x;
  const y1 = to.y;

  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;

  while (true) {
    // Don't check start or end tile
    if ((x0 !== from.x || y0 !== from.y) && (x0 !== x1 || y0 !== y1)) {
      if (blocksLight(map, x0, y0, openDoors)) return false;
    }

    if (x0 === x1 && y0 === y1) break;

    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x0 += sx;
    }
    if (e2 < dx) {
      err += dx;
      y0 += sy;
    }
  }

  return true;
}
